import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import useCustomFetchApi from '../utils/customFetchApi';
import { useAuth } from '../contexts/AuthContext';
import { getCurrentChannel } from '../config';
import Notification from '../components/Notification';
import CountdownTimer from '../components/CountdownTimer';
import PortraitDetailsModal from '../components/PortraitDetailsModal';
import LazyFlag from '../components/LazyFlag';
import LazyImage from '../components/LazyImage';
import MemoryWallStatsBanner from '../components/MemoryWallStatsBanner';
import '../styles/MemoryWall.scss';

const MemoryWall = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, user } = useAuth();
  const [hoveredLocker, setHoveredLocker] = useState(null);
  const [lockers, setLockers] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [offset, setOffset] = useState(0);
  const [notification, setNotification] = useState({ message: '', type: '' });
  const [selectedPortrait, setSelectedPortrait] = useState(null);
  const [selectedPrivateLocker, setSelectedPrivateLocker] = useState(null);
  const [apiExhausted, setApiExhausted] = useState(false); // Track if API has no more data
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const observer = useRef();
  const limit = 10; // Changed to 10 as requested
  const maxLockers = 1000; // Maximum total lockers to display
  const initialFetchDone = useRef(false); // Track if initial fetch is done
  const lastFetchTime = useRef(0); // Track last fetch timestamp to prevent duplicates
  const unclaimedCounter = useRef(0); // Counter for unique unclaimed IDs

  // Refs to track current state without causing re-renders
  const stateRef = useRef({
    offset: 0,
    hasMore: true,
    isLoading: false
  });

  // Optimize initial render - prevent background effects during cold start
  useEffect(() => {
    const body = document.body;
    if (!body.classList.contains('memory-wall-cold-start')) {
      body.classList.add('memory-wall-cold-start');
    }
    const timeout = setTimeout(() => body.classList.remove('memory-wall-cold-start'), 1200);
    if (lockers.length > 1) {
      body.classList.remove('memory-wall-cold-start');
      clearTimeout(timeout);
    }
    return () => {
      body.classList.remove('memory-wall-cold-start');
      clearTimeout(timeout);
    };
  }, [lockers.length]);

  // Update refs when state changes
  useEffect(() => {
    stateRef.current.offset = offset;
    stateRef.current.hasMore = hasMore;
    stateRef.current.isLoading = isLoading;
  }, [offset, hasMore, isLoading]);

  const showNotification = (message, type = 'success') => {
    setNotification({ message, type });
    setTimeout(() => setNotification({ message: '', type: '' }), 4000);
  };

  const api = useCustomFetchApi(showNotification);

  // Language switcher
  const switchLanguageFromMenu = (langKey) => {
    const langCode = langKey === 'us' ? 'en' : langKey;
    try { i18n.changeLanguage(langCode); } catch (_) {}
    const segments = (location.pathname || '/').split('/').filter(Boolean);
    const known = ['en', 'ar', 'my'];
    if (segments.length && known.includes(segments[0])) segments.shift();
    const newPath = `/${langCode}${segments.length ? '/' + segments.join('/') : ''}`;
    navigate(newPath);
    setIsMenuOpen(false);
  };

  // Fetch users from API or generate unclaimed spots
  const fetchUsers = useCallback(async () => {
    const now = Date.now();
    
    // Prevent duplicate calls within 500ms
    if (now - lastFetchTime.current < 500) {
      console.log('Skipping fetch - too soon after last fetch');
      return;
    }
    
    // Use ref values to check conditions to avoid dependency issues
    if (stateRef.current.isLoading || !stateRef.current.hasMore) {
      console.log('Skipping fetch - loading:', stateRef.current.isLoading, 'hasMore:', stateRef.current.hasMore);
      return;
    }

    lastFetchTime.current = now; // Update last fetch time
    setIsLoading(true);
    
    try {
      // Check current locker count
      const currentCount = lockers.length;
      
      // If we've reached max lockers, stop
      if (currentCount >= maxLockers) {
        console.log('Max lockers reached:', currentCount);
        setHasMore(false);
        setIsLoading(false);
        return;
      }
      
      // If API is exhausted, just generate unclaimed spots (no API call)
      if (apiExhausted) {
        console.log('API exhausted - generating pure unclaimed spots');
        
        setLockers(prev => {
          const remaining = maxLockers - prev.length;
          
          if (remaining <= 0) {
            setHasMore(false);
            return prev;
          }
          
          // Generate 10 unclaimed spots (or remaining if less than 10)
          const unclaimedSpots = [];
          for (let i = 0; i < Math.min(remaining, limit); i++) {
            unclaimedCounter.current++;
            unclaimedSpots.push({
              id: `unclaimed-${unclaimedCounter.current}`,
              type: 'unclaimed',
              name: 'Claim This Spot',
            });
          }
          
          const updatedLockers = [...prev, ...unclaimedSpots];
          const finalLockers = updatedLockers.slice(0, maxLockers);
          
          console.log('Generated', unclaimedSpots.length, 'unclaimed spots. Total:', finalLockers.length);
          
          // Continue if we still haven't reached max
          const shouldContinue = finalLockers.length < maxLockers;
          setHasMore(shouldContinue);
          
          return finalLockers;
        });
        
        setIsLoading(false);
        return;
      }

      // API not exhausted yet - make API call
      console.log('Fetching users with offset:', stateRef.current.offset, 'limit:', limit);
      const currentOffset = stateRef.current.offset;
  const { getListApiChannel } = await import('../config');
  const ch = getListApiChannel();
  const data = await api.get(`/list/public?offset=${currentOffset}&limit=${limit}&channel=${ch}`);
      
      console.log('API response:', data);
      
      if (data && data.users && data.users.length > 0) {
        // Filter out users where is_public is false (hidden profiles)
        const publicUsers = data.users.filter(user => user.is_public !== false);
        
        // API has users - process them
        const newLockers = publicUsers.map(user => ({
          id: user.uuid,
          type: 'public',
          name: user.nickname || 'Anonymous',
          nickname: user.nickname || 'Anonymous',
          photo: user.images?.[0]?.image_path, // Use image_path directly
          country: user.country,
          uuid: user.uuid,
          created_at: user.created_at,
          unlock_date: user.unlock_date,
          message: user.message,
          images: user.images,
          joined: user.created_at ? new Date(user.created_at).toLocaleDateString() : '',
        }));

        console.log('Fetched', newLockers.length, 'lockers from API');
        
        // Randomly inject unclaimed spots between profiles
        const lockersWithUnclaimedSpots = [];
        newLockers.forEach((locker, index) => {
          lockersWithUnclaimedSpots.push(locker);
          
          // Randomly inject 1-2 unclaimed spots (30% chance after each locker)
          if (Math.random() < 0.3) {
            const numSpots = Math.random() < 0.5 ? 1 : 2; // 50% chance for 1 or 2 spots
            for (let i = 0; i < numSpots; i++) {
              unclaimedCounter.current++;
              lockersWithUnclaimedSpots.push({
                id: `unclaimed-${unclaimedCounter.current}`,
                type: 'unclaimed',
                name: 'Claim This Spot',
              });
            }
          }
        });
        
        // Filter out duplicates before adding (only check user profiles, not unclaimed)
        setLockers(prev => {
          const existingIds = new Set(prev.filter(l => l.type !== 'unclaimed').map(l => l.id));
          const uniqueNewLockers = lockersWithUnclaimedSpots.filter(locker => 
            locker.type === 'unclaimed' || !existingIds.has(locker.id)
          );
          
          const updatedLockers = [...prev, ...uniqueNewLockers];
          
          // Trim to max 1000 lockers
          const finalLockers = updatedLockers.slice(0, maxLockers);
          
          console.log('Adding', uniqueNewLockers.length, 'lockers (including unclaimed spots)');
          console.log('Total lockers:', finalLockers.length, '/', maxLockers);
          
          return finalLockers;
        });
        
        setOffset(prev => prev + limit);
        
        // Check if we should continue fetching from API
        // If API returned less than limit, it's exhausted
        if (data.users.length < limit) {
          console.log('API exhausted - returned', data.users.length, 'users (expected', limit, ')');
          setApiExhausted(true);
        }
        
        // Always set hasMore to true if we haven't reached 1000 yet
        setLockers(currentLockers => {
          const shouldContinue = currentLockers.length < maxLockers;
          setHasMore(shouldContinue);
          console.log('Has more:', shouldContinue, '(lockers:', currentLockers.length, ')');
          return currentLockers;
        });
        
      } else {
        // API returned no users - mark as exhausted
        console.log('API returned no users - marking as exhausted');
        setApiExhausted(true);
        
        // Continue fetching to generate unclaimed spots
        setHasMore(lockers.length < maxLockers);
      }
    } catch (error) {
      console.error('Error fetching users for memory wall:', error);
      setHasMore(false);
    } finally {
      setIsLoading(false);
    }
  }, [api, limit]);

  // Use ref to avoid dependency issues
  const fetchUsersRef = useRef(fetchUsers);
  useEffect(() => {
    fetchUsersRef.current = fetchUsers;
  }, [fetchUsers]);

  // Create 1 initial empty/unclaimed spot
  useEffect(() => {
    const emptySpots = Array.from({ length: 1 }, (_, index) => ({
      id: `empty-${index}`,
      type: 'unclaimed',
      name: 'Claim This Spot',
    }));
    setLockers(emptySpots);
    initialFetchDone.current = false; // Reset flag when component mounts
  }, []);

  // Initial fetch after empty spots are set - only once
  useEffect(() => {
    if (lockers.length === 1 && 
        lockers.every(l => l.type === 'unclaimed') && 
        !initialFetchDone.current) {
      initialFetchDone.current = true;
      fetchUsersRef.current();
    }
  }, [lockers.length]);

  // Intersection observer for infinite scroll
  const lastLockerRef = useCallback(node => {
    if (stateRef.current.isLoading) {
      console.log('Observer: skipping - already loading');
      return;
    }
    
    if (observer.current) observer.current.disconnect();

    observer.current = new IntersectionObserver(entries => {
      console.log('Observer triggered:', entries[0].isIntersecting, 'hasMore:', stateRef.current.hasMore);
      if (entries[0].isIntersecting && stateRef.current.hasMore) {
        console.log('Calling fetchUsers from observer');
        fetchUsersRef.current();
      }
    }, {
      rootMargin: '200px',
      threshold: 0.01,
    });

    if (node) {
      console.log('Observer: observing last locker');
      observer.current.observe(node);
    }
  }, []);

  // Ensure body scrolling is enabled when component mounts
  useEffect(() => {
    // Remove any modal classes that might prevent scrolling
    document.body.classList.remove('modal-open');
    document.body.style.overflow = 'auto';
    document.body.style.position = 'static';
    document.body.style.width = 'auto';
    
    // Clean up on unmount
    return () => {
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
    };
  }, []);

  // Auto-load more when reaching bottom (works with intersection observer)
  useEffect(() => {
    let scrollTicking = false;
    
    const handleScroll = () => {
      if (scrollTicking) return;
      scrollTicking = true;
      
      requestAnimationFrame(() => {
        if (!stateRef.current.isLoading && stateRef.current.hasMore) {
          const scrollY = window.scrollY;
          const windowHeight = window.innerHeight;
          const documentHeight = document.documentElement.scrollHeight;

          // Trigger fetch when user is near bottom (within 200px)
          if (scrollY + windowHeight >= documentHeight - 200) {
            console.log('Scroll triggered fetch at bottom');
            fetchUsersRef.current();
          }
        }
        scrollTicking = false;
      });
    };

    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const handleLockerClick = (locker) => {
    if (locker.type === 'unclaimed') {
      navigate('/register');
    } else if (locker.type === 'public' && locker.uuid) {
      // Open modal with portrait details
      setSelectedPortrait(locker);
    } else if (locker.type === 'private') {
      // Open fullscreen modal for private lockers
      setSelectedPrivateLocker(locker);
    }
  };

  useEffect(() => {
    // Add stagger animation to lockers
    const lockerElements = document.querySelectorAll('.memory-locker');
    lockerElements.forEach((locker, index) => {
      locker.style.animationDelay = `${index * 0.05}s`;
    });
  }, [lockers.length]);

  return (
    <div className="memory-wall-container">
      {/* Burger Menu Button */}
      <button
        className={`menu-button ${isMenuOpen ? 'open' : ''}`}
        aria-label="Open menu"
        onClick={() => setIsMenuOpen(v => !v)}
      >
        <span />
        <span />
        <span />
      </button>
      
      {/* Menu Overlay & Panel */}
      {isMenuOpen && (
        <>
          <div className="menu-overlay" onClick={() => setIsMenuOpen(false)} />
          <div className="menu-panel">
            <div className="menu-section">
              <h4>{t('memoryWall.menu.language')}</h4>
              <div className="menu-language-row">
                <button type="button" className="menu-link menu-language-row" onClick={() => switchLanguageFromMenu('us')}>US</button>
                <button type="button" className="menu-link menu-language-row" onClick={() => switchLanguageFromMenu('my')}>MY</button>
                <button type="button" className="menu-link menu-language-row" onClick={() => switchLanguageFromMenu('ar')}>AR</button>
              </div>
            </div>
            <div className="menu-section">
              <h4>{t('memoryWall.menu.account')}</h4>
              {isAuthenticated() ? (
                <Link to={`/${i18n.language}/${getCurrentChannel()}/dashboard`} className="menu-link" onClick={() => setIsMenuOpen(false)}>
                  👋 {user?.nickname || t('memoryWall.menu.dashboard')}
                </Link>
              ) : (
                <Link to="/login" className="menu-link" onClick={() => setIsMenuOpen(false)}>
                  {t('memoryWall.menu.login')}
                </Link>
              )}
            </div>
            <div className="menu-section">
              <Link to="/" className="menu-link" onClick={() => setIsMenuOpen(false)}>
                🏠 {t('memoryWall.menu.home')}
              </Link>
            </div>
          </div>
        </>
      )}
      
      <Notification
        message={notification.message}
        type={notification.type}
        onClose={() => setNotification({ message: '', type: '' })}
      />
      
      {/* Cosmic background */}
      <div className="cosmic-background">
        <div className="stars"></div>
        <div className="stars-2"></div>
        <div className="stars-3"></div>
      </div>

      {/* Header */}
      <div className="memory-wall-header">
        <h1 className="wall-title">{t('memoryWall.title')}</h1>
        <div className="title-underline"></div>
        <p className="wall-subtitle">
          {t('memoryWall.subtitle')}
        </p>
      </div>
      
      {/* Statistics Banner */}
      <MemoryWallStatsBanner />

      {/* Locker Grid */}
      <div className="locker-grid">
        {lockers.map((locker, index) => (
          <div
            key={locker.id}
            ref={index === lockers.length - 1 ? lastLockerRef : null}
            className={`locker-tile ${locker.type} ${hoveredLocker === locker.id ? 'hovered' : ''}`}
            onMouseEnter={() => setHoveredLocker(locker.id)}
            onMouseLeave={() => setHoveredLocker(null)}
            onClick={() => handleLockerClick(locker)}
          >
            {/* Top ventilation slits */}
            <div className="locker-vents-top">
              <div className="vent-slit"></div>
              <div className="vent-slit"></div>
              <div className="vent-slit"></div>
            </div>

            {/* Door handle - left side */}
            <div className="locker-handle"></div>

            {/* Bottom ventilation slits */}
            <div className="locker-vents-bottom">
              <div className="vent-slit"></div>
              <div className="vent-slit"></div>
              <div className="vent-slit"></div>
            </div>

            {/* Status label instead of number */}
            <div className="locker-status">
              <span>{t(`memoryWall.status.${locker.type}`)}</span>
            </div>

            {/* Country flag tag for public lockers - top left */}
            {locker.type === 'public' && locker.country && (
              <div className="locker-country-flag">
                <LazyFlag
                  src={`/flags/${locker.country.toLowerCase()}.webp`}
                  alt={`${locker.country} flag`}
                  className="flag-icon"
                />
              </div>
            )}

            {/* Hover glow effect */}
            <div className="locker-glow"></div>

            {/* Locker Content */}
            <div className="locker-content">
              {locker.type === 'public' && (
                <>
                  <LazyImage
                    src={locker.photo}
                    alt={locker.name}
                    className="locker-photo"
                    placeholder="linear-gradient(135deg, #1a1f2e 0%, #23263f 25%, #3d3154 50%, #23263f 75%, #1a1f2e 100%)"
                    lazyOptions={{
                      rootMargin: '200px',
                      threshold: 0.01
                    }}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      borderRadius: '4px'
                    }}
                  />
                  <div className="locker-name">{locker.name}</div>
                </>
              )}

              {locker.type === 'private' && (
                <>
                  <div className="locker-photo-frame"></div>
                  <div className="locker-private-overlay">
                    <img src="/icon/locker.png" alt="locked" className="lock-icon-image" />
                  </div>
                  {locker.unlock_date && (
                    <CountdownTimer unlockDate={locker.unlock_date} />
                  )}
                </>
              )}

              {locker.type === 'unclaimed' && (
                <div className="locker-unclaimed-content">
                  <div className="empty-icon">
                    <i className="fas fa-star"></i>
                  </div>
                  <button className="claim-button">
                    {t('memoryWall.claimButton')}
                  </button>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Loading indicator */}
      {isLoading && (
        <div style={{ textAlign: 'center', padding: '40px', position: 'relative', zIndex: 1 }}>
          <div style={{
            width: '40px',
            height: '40px',
            border: '4px solid rgba(167, 139, 250, 0.2)',
            borderTop: '4px solid #a78bfa',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto'
          }} />
          <p style={{ color: '#cbd5e0', marginTop: '16px', fontSize: '0.9rem' }}>
            {t('memoryWall.loading')}
          </p>
        </div>
      )}

      {/* Load More button fallback for mobile */}
      {!isLoading && hasMore && (
        <div style={{ textAlign: 'center', padding: '20px', position: 'relative', zIndex: 1 }}>
          <button
            onClick={() => {
              console.log('Load More button clicked');
              fetchUsersRef.current();
            }}
            style={{
              padding: '12px 32px',
              fontSize: '1rem',
              fontWeight: '600',
              color: 'white',
              background: 'linear-gradient(135deg, #a78bfa, #60a5fa)',
              border: 'none',
              borderRadius: '50px',
              cursor: 'pointer',
              boxShadow: '0 4px 12px rgba(167, 139, 250, 0.3)',
              transition: 'all 0.3s ease',
            }}
            onMouseEnter={(e) => {
              e.target.style.transform = 'translateY(-2px)';
              e.target.style.boxShadow = '0 6px 20px rgba(167, 139, 250, 0.5)';
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = 'translateY(0)';
              e.target.style.boxShadow = '0 4px 12px rgba(167, 139, 250, 0.3)';
            }}
          >
            {t('memoryWall.loadMore')}
          </button>
        </div>
      )}

      {/* End of results message */}
      {!hasMore && lockers.length > 0 && (
        <div style={{ textAlign: 'center', padding: '40px', position: 'relative', zIndex: 1 }}>
          <p style={{ color: 'rgba(255, 255, 255, 0.5)', fontSize: '0.9rem' }}>
            {t('memoryWall.endMessage')}
          </p>
        </div>
      )}

      {/* Bottom CTA */}
      <div className="memory-wall-cta">
        <button className="cta-button" onClick={() => navigate('/register')}>
          <span className="button-glow"></span>
          <i className="fas fa-star"></i>
          <span>{t('memoryWall.ctaButton')}</span>
        </button>
        <p className="cta-subtitle">{t('memoryWall.ctaSubtitle')}</p>
      </div>

      {/* Portrait Details Modal */}
      {selectedPortrait && (
        <PortraitDetailsModal
          portrait={selectedPortrait}
          onClose={() => setSelectedPortrait(null)}
          onOutsideClick={(e) => {
            if (e.target.id === 'portrait-popup') {
              setSelectedPortrait(null);
            }
          }}
        />
      )}

      {/* Private Locker Fullscreen Modal */}
      {selectedPrivateLocker && (
        <div 
          className="private-locker-fullscreen-modal"
          onClick={() => setSelectedPrivateLocker(null)}
        >
          <div className="private-modal-content" onClick={(e) => e.stopPropagation()}>
            <button 
              className="private-modal-close" 
              onClick={() => setSelectedPrivateLocker(null)}
            >
              ×
            </button>
            
            <div className="private-modal-locker">
              {/* Locker visual */}
              <div className="modal-locker-display">
                <div className="locker-vents-top">
                  <div className="vent-slit"></div>
                  <div className="vent-slit"></div>
                  <div className="vent-slit"></div>
                </div>

                <div className="locker-handle"></div>

                <div className="locker-vents-bottom">
                  <div className="vent-slit"></div>
                  <div className="vent-slit"></div>
                  <div className="vent-slit"></div>
                </div>

                <div className="locker-photo-frame"></div>
                
                <div className="locker-private-overlay">
                  <img src="/icon/locker.png" alt="locked" className="lock-icon-image" />
                </div>

                {selectedPrivateLocker.unlock_date && (
                  <CountdownTimer unlockDate={selectedPrivateLocker.unlock_date} />
                )}
              </div>
              
              <div className="private-modal-info">
                <h2>{t('memoryWall.privateModal.title')}</h2>
                <p>{t('memoryWall.privateModal.description')}</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MemoryWall;
